import { CreatedByUser, Project } from "@/api/query/useProjectQuery";
import { FIELDS } from "@/api/types/enums";
import InputText from "@/components/common/InputText";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import Select, { StylesConfig } from "react-select";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { Options } from "@/components/views/ganntView/GanttView";
import CalendarSvg from "../../assets/svg/Calendar.svg";
import CrossSvg from "../../assets/svg/crossNew.svg";
import { SlidersHorizontal } from "lucide-react";
import DownArrow from "../../assets/svg/DownArrow.svg";
import DownArrowWhite from "../../assets/svg/downArrowWhite.svg";
import SearchSvg from "@/assets/v2/svg/search.svg";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";
import {
  ProjectStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import { Calendar } from "@/components/ui/calendar";
import dateFormater from "@/helperFuntions/dateFormater";
import useDebounce from "@/hooks/useDebounce";
import useAllPortfoliosQuery, {
  Portfolios,
  Program,
} from "@/api/query/useAllPortFoliosQuery";
import useOrganisationDetailsQuery from "@/api/query/useOrganisationDetailsQuery";
import useProjectFieldLabels from "@/hooks/useProjectFieldLabels";
import useReferenceDataQuery from "@/api/query/useReferenceDataQuery";

type ProjectPhase = { projectPhaseId: string; phaseName: string };
type ProjectPriority = { priorityId?: string; projectPriorityId?: string; priorityName: string };
type ProjectType = { projectTypeId: string; projectTypeName: string };
type Workstream = { workstreamId: string; workstreamName: string };
type ProjectCategory = { categoryId: string; categoryName: string };

type FilterProps = {
  project: Project[] | undefined;
  fieldToShow: FIELDS[];
  filteredData: (data: Project[] | undefined) => void;
  view?: string;
  appleed?: (data: boolean) => void;
};

export type TaskFilterRef = {
  callFilter: () => void;
};

interface FilterState {
  portfolioId: string;
  programId: string;
  projectManager: string[];
  status: string[];
  projectType: string[];
  projectPhase: string[];
  projectPriority: string[];
  workstream: string[];
  projectCategory: string[];
  delay: string[];
  budget: string[];
  tags: string[];
  date: DateRange | undefined;
}

const getSelectedValues = (vals: unknown): string[] => {
  if (!vals) return [];
  if (Array.isArray(vals)) {
    return vals.map((v) => (v as Options).value);
  }
  return [(vals as Options).value];
};

const reactSelectStyle: StylesConfig<Options, boolean> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "32px",
    borderRadius: "8px",
    border: state.isFocused ? "1px solid #535EF9" : "1px solid #E2E8F0",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    cursor: "pointer",
    transition: "all 300ms ease",
    "&:hover": {
      border: state.isFocused ? "1px solid #535EF9" : "1px solid #CBD5E1",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 8px",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "2px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#535EF9" : "#94A3B8",
    padding: "0 8px 0 0",
    "&:hover": {
      color: "#535EF9",
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "12px",
    border: "1px solid #F1F5F9",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
    padding: "4px",
    background: "white",
    zIndex: 60,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: "0",
    maxHeight: "180px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#94A3B8",
    fontSize: "12px",
    fontWeight: "400",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#1E293B",
    fontSize: "12px",
    fontWeight: "600",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#F5F6FF",
    borderRadius: "6px",
    border: "1px solid #535EF940",
    margin: "1px 2px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#535EF9",
    fontSize: "11px",
    fontWeight: "600",
    padding: "1px 6px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#535EF9",
    ":hover": {
      backgroundColor: "#535EF920",
      color: "#3B42C4",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    fontSize: "12px",
    fontWeight: "600",
    color: "#94A3B8",
    padding: "12px 0",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "12px",
    fontWeight: "600",
    borderRadius: "6px",
    padding: "7px 10px",
    color: state.isSelected ? "white" : "#475569",
    background: state.isSelected ? "#535EF9" : "white",
    "&:hover": {
      color: "white",
      background: "#535EF9CC",
    },
  }),
};

const initialFilterState: FilterState = {
  portfolioId: "",
  programId: "",
  projectManager: [],
  status: [],
  projectType: [],
  projectPhase: [],
  projectPriority: [],
  workstream: [],
  projectCategory: [],
  delay: [],
  budget: [],
  tags: [],
  date: undefined,
};

const ProjectFilter = (props: FilterProps) => {
  const CPILabels = [
    "SIGNIFICANT_BUDGET",
    "MODERATE_BUDGET",
    "ON_BUDGET",
    "UNDER_BUDGET",
  ] as const;

  const SPILabels = [
    "SIGNIFICANT_DELAY",
    "MODERATE_DELAY",
    "ON_TIME",
    "AHEAD_OF_SCHEDULE",
  ] as const;

  const [popOverCLose, setPopOverCLose] = useState(false);
  const [filterApplyed, setFilterApplyed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const projectFieldLabels = useProjectFieldLabels();

  const [filter, setFilter] = useState<FilterState>(initialFilterState);
  const [tempFilter, setTempFilter] = useState<FilterState>(initialFilterState);

  useEffect(() => {
    setTempFilter(filter);
  }, [filter]);

  useEffect(() => {
    const statusParam = searchParams.get("status");
    const delayParam = searchParams.get("delay");
    const budgetParam = searchParams.get("budget");

    if (statusParam || delayParam || budgetParam) {
      setFilter((prev) => ({
        ...prev,
        status: statusParam ? [statusParam] : prev.status,
        delay: delayParam ? [delayParam] : prev.delay,
        budget: budgetParam ? [budgetParam] : prev.budget,
      }));
    }
  }, [props.project, searchParams]);

  const [searchString, setSearchString] = useDebounce(
    searchParams.get("search") ?? "",
    400
  );

  const searchParamValue = searchParams.get("search") ?? "";
  const [searchValue, setSearchValue] = useState(searchParamValue);

  useEffect(() => {
    setSearchValue(searchParamValue);
    setSearchString(searchParamValue);
  }, [searchParamValue]);

  useEffect(() => {
    ApplyFilter();
  }, [
    searchString,
    filter.projectManager,
    filter.status,
    filter.delay,
    filter.budget,
    filter.date,
    filter.portfolioId,
    filter.programId,
    filter.projectType,
    filter.projectPhase,
    filter.projectPriority,
    filter.workstream,
    filter.projectCategory,
    filter.tags,
    props.project,
  ]);

  const projectManagerName = (data: CreatedByUser): string => {
    if (data.firstName && data.lastName) {
      return `${data.firstName} ${data.lastName}`;
    }
    return data.email;
  };

  const projectManagerOptions = useMemo((): Options[] => {
    const managersMap = new Map<string, string>();
    props.project?.forEach((item) => {
      const hasExplicitPM = item.assignedUsers?.some(
        (d) => d.projectRole === UserRoleEnumValue.PROJECT_MANAGER
      );

      item.assignedUsers
        ?.filter(
          (d) =>
            (hasExplicitPM && d.projectRole === UserRoleEnumValue.PROJECT_MANAGER) ||
            (!hasExplicitPM && d.projectRole === UserRoleEnumValue.ADMINISTRATOR)
        )
        .forEach((element) => {
          const val = element.user.userId || element.user.email;
          if (val && !managersMap.has(val)) {
            managersMap.set(val, projectManagerName(element.user));
          }
        });
    });

    return Array.from(managersMap.entries()).map(([value, label]) => ({
      label,
      value,
    }));
  }, [props.project]);

  const statusOptions = useMemo((): Options[] => {
    const statusSet = new Set<string>();
    props.project?.forEach((item) => {
      if (item.status) {
        statusSet.add(item.status);
      }
    });
    return Array.from(statusSet).map((s) => ({
      label: t(s, s),
      value: s,
    }));
  }, [props.project, t]);

  const getProjectTypeName = (p: Project): string => {
    if (!p.projectType) return "";
    if (typeof p.projectType === "object" && "projectTypeName" in p.projectType) {
      return (p.projectType as ProjectType).projectTypeName || "";
    }
    return String(p.projectType);
  };

  const getProjectTypeId = (p: Project): string => {
    if (!p.projectType) return "";
    if (typeof p.projectType === "object" && "projectTypeId" in p.projectType) {
      return (p.projectType as ProjectType).projectTypeId || "";
    }
    return String(p.projectType);
  };

  const getWorkstreamName = (p: Project): string => {
    if (!p.workstream) return "";
    if (typeof p.workstream === "object" && "workstreamName" in p.workstream) {
      return (p.workstream as Workstream).workstreamName || "";
    }
    return String(p.workstream);
  };

  const getWorkstreamId = (p: Project): string => {
    if (!p.workstream) return "";
    if (typeof p.workstream === "object" && "workstreamId" in p.workstream) {
      return (p.workstream as Workstream).workstreamId || "";
    }
    return String(p.workstream);
  };

  const getProjectCategoryName = (p: Project): string => {
    if (!p.projectCategory) return "";
    if (typeof p.projectCategory === "object" && "categoryName" in p.projectCategory) {
      return (p.projectCategory as ProjectCategory).categoryName || "";
    }
    return String(p.projectCategory);
  };

  const getProjectCategoryId = (p: Project): string => {
    if (!p.projectCategory) return "";
    if (typeof p.projectCategory === "object" && "categoryId" in p.projectCategory) {
      return (p.projectCategory as ProjectCategory).categoryId || "";
    }
    return String(p.projectCategory);
  };

  const getProjectPhaseName = (p: Project): string => {
    if (!p.projectPhase) return "";
    if (typeof p.projectPhase === "object" && "phaseName" in p.projectPhase) {
      return (p.projectPhase as ProjectPhase).phaseName || "";
    }
    return String(p.projectPhase);
  };

  const getProjectPhaseId = (p: Project): string => {
    if (!p.projectPhase) return "";
    if (typeof p.projectPhase === "object" && "projectPhaseId" in p.projectPhase) {
      return (p.projectPhase as ProjectPhase).projectPhaseId || "";
    }
    return String(p.projectPhase);
  };

  const getProjectPriorityName = (p: Project): string => {
    if (!p.priority) return "";
    if (typeof p.priority === "object" && "priorityName" in p.priority) {
      return (p.priority as ProjectPriority).priorityName || "";
    }
    return String(p.priority);
  };

  const getProjectPriorityId = (p: Project): string => {
    if (!p.priority) return "";
    if (typeof p.priority === "object" && "projectPriorityId" in p.priority) {
      return (p.priority as ProjectPriority).projectPriorityId || (p.priority as ProjectPriority).priorityId || "";
    }
    return String(p.priority);
  };

  const projectTypeOptions = useMemo((): Options[] => {
    const map = new Map<string, string>();
    props.project?.forEach((item) => {
      const typeName = getProjectTypeName(item);
      const typeId = getProjectTypeId(item) || typeName;
      if (typeId && typeName && !map.has(typeId)) {
        map.set(typeId, typeName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [props.project]);

  const workstreamOptions = useMemo((): Options[] => {
    const map = new Map<string, string>();
    props.project?.forEach((item) => {
      const wsName = getWorkstreamName(item);
      const wsId = getWorkstreamId(item) || wsName;
      if (wsId && wsName && !map.has(wsId)) {
        map.set(wsId, wsName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [props.project]);

  const projectCategoryOptions = useMemo((): Options[] => {
    const map = new Map<string, string>();
    props.project?.forEach((item) => {
      const categoryName = getProjectCategoryName(item);
      const catId = getProjectCategoryId(item) || categoryName;
      if (catId && categoryName && !map.has(catId)) {
        map.set(catId, categoryName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [props.project]);

  const projectPhaseOptions = useMemo((): Options[] => {
    const map = new Map<string, string>();
    props.project?.forEach((item) => {
      const phaseName = getProjectPhaseName(item);
      const phaseId = getProjectPhaseId(item) || phaseName;
      if (phaseId && phaseName && !map.has(phaseId)) {
        map.set(phaseId, phaseName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [props.project]);

  const projectPriorityOptions = useMemo((): Options[] => {
    const map = new Map<string, string>();
    props.project?.forEach((item) => {
      const priorityName = getProjectPriorityName(item);
      const priorityId = getProjectPriorityId(item) || priorityName;
      if (priorityId && priorityName && !map.has(priorityId)) {
        map.set(priorityId, priorityName);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [props.project]);

  const delayOptions = useMemo(
    (): Options[] =>
      SPILabels.map((s) => ({
        label: t(s),
        value: s,
      })),
    [SPILabels, t]
  );

  const budgetOptions = useMemo(
    (): Options[] =>
      CPILabels.map((s) => ({
        label: t(s),
        value: s,
      })),
    [CPILabels, t]
  );

  const organisationId = localStorage.getItem("organisation-id");

  const { data, refetch, isFetched } = useOrganisationDetailsQuery(
    organisationId ?? "",
    false
  );

  useEffect(() => {
    if (!isFetched) {
      refetch();
    }
  }, [isFetched]);

  const tagsQuery = useReferenceDataQuery("tag", true);

  const tagsOptions = useMemo((): Options[] => {
    if (tagsQuery.data?.data?.data) {
      return tagsQuery.data.data.data.map((tag: any) => ({
        value: tag.tagId,
        label: tag.tagName,
      }));
    }
    return [];
  }, [tagsQuery.data?.data?.data]);

  const [budgetRange, setBudgetRange] = useState({
    onBudgetMax: 5,
    moderateBudgetMax: 20,
  });

  const [delayRange, setDelayRange] = useState({
    onTimeDelayMax: 5,
    moderateDelayMax: 20,
  });

  useEffect(() => {
    setBudgetRange({
      onBudgetMax: data?.data.data.onBudgetMax ?? 5,
      moderateBudgetMax: data?.data.data.moderateBudgetMax ?? 20,
    });
    setDelayRange({
      onTimeDelayMax: data?.data.data.onTimeDelayMax ?? 5,
      moderateDelayMax: data?.data.data.moderateDelayMax ?? 20,
    });
  }, [data]);


  const delayThresholds = useMemo(
    () => ({
      significant: 100 - delayRange.moderateDelayMax,
      moderate: 100 - delayRange.onTimeDelayMax,
      onTime: 100,
    }),
    [delayRange]
  );

  const getDelayCategory = (spi: number, isNotStarted: boolean, thresholds: any) => {
    const { significant, moderate, onTime } = thresholds;

    if (isNotStarted) return "ON_TIME";
    if (spi < significant) return "SIGNIFICANT_DELAY";
    if (spi < moderate) return "MODERATE_DELAY";
    if (spi <= onTime) return "ON_TIME";
    return "AHEAD_OF_SCHEDULE";
  };

  useEffect(() => {
    ApplyFilter();
  }, [searchString]);

  const ApplyFilter = () => {
    let filtered = props.project || [];

    if (searchString) {
      filtered = filtered.filter((p) => {
        const projectTypeName = p.projectType
          ? typeof p.projectType === "object" && "projectTypeName" in p.projectType
            ? (p.projectType as ProjectType).projectTypeName
            : String(p.projectType)
          : "";
        const workstreamName = p.workstream
          ? typeof p.workstream === "object" && "workstreamName" in p.workstream
            ? (p.workstream as Workstream).workstreamName
            : String(p.workstream)
          : "";
        const phaseName = p.projectPhase
          ? typeof p.projectPhase === "object" && "phaseName" in p.projectPhase
            ? (p.projectPhase as ProjectPhase).phaseName
            : String(p.projectPhase)
          : "";
        const priorityName = p.priority
          ? typeof p.priority === "object" && "priorityName" in p.priority
            ? (p.priority as ProjectPriority).priorityName
            : String(p.priority)
          : "";
        const categoryName = p.projectCategory
          ? typeof p.projectCategory === "object" && "categoryName" in p.projectCategory
            ? (p.projectCategory as ProjectCategory).categoryName
            : String(p.projectCategory)
          : "";

        return (
          p.projectName.toLowerCase().includes(searchString.toLowerCase()) ||
          p.projectCode?.toLowerCase().includes(searchString.toLowerCase()) ||
          projectTypeName.toLowerCase().includes(searchString.toLowerCase()) ||
          workstreamName.toLowerCase().includes(searchString.toLowerCase()) ||
          phaseName.toLowerCase().includes(searchString.toLowerCase()) ||
          priorityName.toLowerCase().includes(searchString.toLowerCase()) ||
          categoryName.toLowerCase().includes(searchString.toLowerCase())
        );
      });
    }

    // Project Manager Filter: match only projects whose assigned PM (or Admin PM fallback) is selected
    if (filter.projectManager && filter.projectManager.length > 0) {
      filtered = filtered.filter((proj) => {
        const hasExplicitPM = proj.assignedUsers?.some(
          (au) => au.projectRole === UserRoleEnumValue.PROJECT_MANAGER
        );

        return proj.assignedUsers?.some((au) => {
          const isManagerRole = hasExplicitPM
            ? au.projectRole === UserRoleEnumValue.PROJECT_MANAGER
            : au.projectRole === UserRoleEnumValue.ADMINISTRATOR;

          if (!isManagerRole) return false;

          const pmId = au.user?.userId;
          const pmEmail = au.user?.email;
          return filter.projectManager.some((id) => id === pmId || id === pmEmail);
        });
      });
    }

    // Status Filter (multi-select)
    if (filter.status && filter.status.length > 0) {
      filtered = filtered.filter((proj) => filter.status.includes(proj.status));
    }

    // Delay Filter (multi-select)
    if (filter.delay && filter.delay.length > 0) {
      filtered = filtered.filter((p: any) => {
        if (p.status === ProjectStatusEnumValue.CANCELLED) {
          return false;
        }

        const actualStart = p.projectActualStartDate
          ? new Date(p.projectActualStartDate)
          : null;
        if (actualStart) actualStart.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const isNotStarted =
          p.status === ProjectStatusEnumValue.NOT_STARTED ||
          Boolean(actualStart && actualStart > today);

        const category =
          typeof p.spi === "number"
            ? getDelayCategory(p.spi, isNotStarted, delayThresholds)
            : "ON_TIME";

        return category && filter.delay.includes(category);
      });
    }

    // Budget Filter (multi-select)
    if (filter.budget && filter.budget.length > 0) {
      filtered = filtered.filter((p) => {
        const estimated = Number(p.estimatedBudget) || 0;
        const fbv =
          p.forecastBudgetVariance !== undefined && p.forecastBudgetVariance !== null
            ? Number(p.forecastBudgetVariance)
            : estimated > 0 && p.reCalculateBudget != null && Number(p.reCalculateBudget) > 0
            ? ((Number(p.reCalculateBudget) - estimated) / estimated) * 100
            : null;

        if (fbv === null || isNaN(fbv)) return false;

        const onBudget = budgetRange.onBudgetMax ?? 5;
        const moderate = budgetRange.moderateBudgetMax ?? 20;

        return filter.budget.some((budgetType) => {
          if (budgetType === "SIGNIFICANT_BUDGET") {
            return fbv > moderate;
          }
          if (budgetType === "MODERATE_BUDGET") {
            return fbv > onBudget && fbv <= moderate;
          }
          if (budgetType === "ON_BUDGET") {
            return fbv > 0 && fbv <= onBudget;
          }
          if (budgetType === "UNDER_BUDGET") {
            return fbv <= 0;
          }
          return false;
        });
      });
    }

    // Portfolio Filter
    if (filter.portfolioId) {
      filtered = filtered.filter(
        (p) => p.portfolio?.portfolioId === filter.portfolioId
      );
    }

    // Program Filter
    if (filter.programId) {
      filtered = filtered.filter(
        (p) => p.program?.programId === filter.programId
      );
    }

    // Project Type Filter (multi-select)
    if (filter.projectType && filter.projectType.length > 0) {
      filtered = filtered.filter((proj) => {
        const typeId = getProjectTypeId(proj);
        const typeName = getProjectTypeName(proj);
        return (
          filter.projectType.includes(typeId) ||
          filter.projectType.includes(typeName)
        );
      });
    }

    // Project Phase Filter (multi-select)
    if (filter.projectPhase && filter.projectPhase.length > 0) {
      filtered = filtered.filter((proj) => {
        const phaseId = getProjectPhaseId(proj);
        const phaseName = getProjectPhaseName(proj);
        return (
          filter.projectPhase.includes(phaseId) ||
          filter.projectPhase.includes(phaseName)
        );
      });
    }

    // Project Priority Filter (multi-select)
    if (filter.projectPriority && filter.projectPriority.length > 0) {
      filtered = filtered.filter((proj) => {
        const priorityId = getProjectPriorityId(proj);
        const priorityName = getProjectPriorityName(proj);
        return (
          filter.projectPriority.includes(priorityId) ||
          filter.projectPriority.includes(priorityName)
        );
      });
    }

    // Workstream Filter (multi-select)
    if (filter.workstream && filter.workstream.length > 0) {
      filtered = filtered.filter((proj) => {
        const wsId = getWorkstreamId(proj);
        const wsName = getWorkstreamName(proj);
        return (
          filter.workstream.includes(wsId) ||
          filter.workstream.includes(wsName)
        );
      });
    }

    // Project Category Filter (multi-select)
    if (filter.projectCategory && filter.projectCategory.length > 0) {
      filtered = filtered.filter((proj) => {
        const catId = getProjectCategoryId(proj);
        const catName = getProjectCategoryName(proj);
        return (
          filter.projectCategory.includes(catId) ||
          filter.projectCategory.includes(catName)
        );
      });
    }

    // Tags Filter (multi-select)
    if (filter.tags && filter.tags.length > 0) {
      filtered = filtered.filter((proj) => {
        if (!proj.tags || proj.tags.length === 0) return false;
        // proj.tags is an array of tagIds
        return filter.tags.some(tagId => proj.tags?.includes(tagId));
      });
    }

    // Date Range Filter
    if (filter.date?.from && filter.date?.to) {
      const fromDate = new Date(filter.date.from).setHours(0, 0, 0, 0);
      const toDate = new Date(filter.date.to).setHours(23, 59, 59, 999);
      filtered = filtered.filter((proj) => {
        const endDate = new Date(proj.estimatedEndDate || "").getTime();
        return endDate >= fromDate && endDate <= toDate;
      });
    }

    const isFiltered = Boolean(
      (filter.projectManager && filter.projectManager.length > 0) ||
      (filter.status && filter.status.length > 0) ||
      (filter.delay && filter.delay.length > 0) ||
      (filter.budget && filter.budget.length > 0) ||
      (filter.projectType && filter.projectType.length > 0) ||
      (filter.projectPhase && filter.projectPhase.length > 0) ||
      (filter.projectPriority && filter.projectPriority.length > 0) ||
      (filter.workstream && filter.workstream.length > 0) ||
      (filter.projectCategory && filter.projectCategory.length > 0) ||
      (filter.tags && filter.tags.length > 0) ||
      filter.portfolioId ||
      filter.programId ||
      (filter.date?.from && filter.date?.to)
    );

    setFilterApplyed(isFiltered);
    props.appleed?.(isFiltered);
    props.filteredData(filtered);
  };

  const resetFilter = () => {
    setFilter(initialFilterState);
    setTempFilter(initialFilterState);
    setSearchParams({});
    setFilterApplyed(false);
    props.filteredData(props.project);
  };

  const [portfolios, setPortfolios] = useState<Options[]>([]);
  const [programs, setPrograms] = useState<Options[]>([]);
  const [allPortfolios, setAllPortfolios] = useState<{
    programs: Program[];
    portfolios: Portfolios[];
  }>();

  const allPortfoliosQuery = useAllPortfoliosQuery(false);

  useEffect(() => {
    if (!allPortfoliosQuery.isFetched) {
      allPortfoliosQuery.refetch();
    }

    const portData = allPortfoliosQuery.data?.data.data;
    setAllPortfolios(portData);
    setPortfolios([
      ...(portData?.portfolios ?? []).map(({ portfolioId, portfolioName }) => ({
        value: portfolioId,
        label: portfolioName,
      })),
    ]);
  }, [allPortfoliosQuery.data?.data.data, t]);

  useEffect(() => {
    const selectedPortfolio = allPortfolios?.portfolios.find(
      ({ portfolioId }) => portfolioId === tempFilter.portfolioId
    );

    if (selectedPortfolio && selectedPortfolio?.programs?.length) {
      setPrograms(
        selectedPortfolio?.programs?.map(({ programId, programName }) => ({
          value: programId,
          label: programName,
        })) ?? []
      );
    } else {
      setPrograms(
        allPortfolios?.programs?.map(({ programId, programName }) => ({
          value: programId,
          label: programName,
        })) ?? []
      );
    }
  }, [tempFilter.portfolioId, allPortfoliosQuery.data?.data.data, allPortfolios]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filter.portfolioId) count++;
    if (filter.programId) count++;
    if (filter.projectManager?.length) count += filter.projectManager.length;
    if (filter.status?.length) count += filter.status.length;
    if (filter.projectType?.length) count += filter.projectType.length;
    if (filter.workstream?.length) count += filter.workstream.length;
    if (filter.projectCategory?.length) count += filter.projectCategory.length;
    if (filter.delay?.length) count += filter.delay.length;
    if (filter.budget?.length) count += filter.budget.length;
    if (filter.date?.from && filter.date?.to) count++;
    return count;
  }, [filter]);

  return (
    <div>
      <div className="flex w-full justify-between items-center gap-2">
        <div className="flex justify-between w-full gap-2 text-gray-500">
          <div className="flex justify-between items-center gap-6 px-3 w-full relative">
            <div className="relative inline-flex">
              <Popover
                open={popOverCLose}
                onOpenChange={(open) => {
                  setPopOverCLose(open);
                  if (open) {
                    setTempFilter(filter);
                  }
                }}
              >
                <PopoverTrigger asChild className="w-full">
                  <Button
                    type="button"
                    variant={"outline"}
                    className="h-10 rounded-xl px-3 border border-slate-200/80 flex items-center gap-2 text-xs font-bold transition-all duration-300 hover:scale-[1.02] cursor-pointer shadow-sm min-w-[110px]"
                    style={{
                      backgroundColor:
                        filterApplyed || searchParams.size > 0 ? "#535EF9" : "",
                      color:
                        filterApplyed || searchParams.size > 0 ? "#ffffff" : "",
                    }}
                    onClick={() => setPopOverCLose(!popOverCLose)}
                  >
                    <div className="flex justify-between gap-2 items-center w-full">
                      <div className="flex items-center gap-1.5">
                        <SlidersHorizontal
                          className={`w-3.5 h-3.5 ${
                            filterApplyed || searchParams.size > 0
                              ? "text-white"
                              : "text-slate-500"
                          }`}
                        />
                        <span>{t("Filters")}</span>
                        {activeFiltersCount > 0 && (
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold shrink-0 leading-none ${
                              filterApplyed || searchParams.size > 0
                                ? "bg-white text-[#535EF9]"
                                : "bg-[#535EF9] text-white"
                            }`}
                          >
                            {activeFiltersCount}
                          </span>
                        )}
                      </div>
                      <div className="w-fit">
                        <img
                          src={
                            filterApplyed || searchParams.size > 0
                              ? DownArrowWhite
                              : DownArrow
                          }
                          alt="arrow"
                        />
                      </div>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[440px] p-4 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
                  align="start"
                >
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div className="text-sm font-bold text-slate-800">
                      {t("Filters")}
                    </div>
                    {activeFiltersCount > 0 && (
                      <span className="text-xs font-semibold text-[#535EF9] bg-[#535EF9]/10 px-2 py-0.5 rounded-full">
                        {activeFiltersCount} active
                      </span>
                    )}
                  </div>

                  {props.project && (
                    <div className="flex flex-col gap-3">
                      {/* Portfolio Filter */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("portfolio", "Portfolio")}
                        </span>
                        <div className="flex-1">
                          <Select
                            placeholder={t("All Portfolios")}
                            options={portfolios}
                            value={
                              portfolios.find(
                                (t) => t.value === tempFilter.portfolioId
                              ) || null
                            }
                            isClearable={true}
                            onChange={(e) => {
                              const val = e as Options | null;
                              setTempFilter((prev) => ({
                                ...prev,
                                portfolioId: val ? val.value : "",
                                programId: "",
                              }));
                            }}
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Program Filter */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("program", "Program")}
                        </span>
                        <div className="flex-1">
                          <Select
                            placeholder={t("All Programs")}
                            options={programs}
                            value={
                              programs.find(
                                (t) => t.value === tempFilter.programId
                              ) || null
                            }
                            isClearable={true}
                            onChange={(e) => {
                              const val = e as Options | null;
                              setTempFilter((prev) => ({
                                ...prev,
                                programId: val ? val.value : "",
                              }));
                            }}
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Project Manager Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("project_manager_filter", "Project Manager")}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={t("all_project_managers", "All Project Managers")}
                            options={projectManagerOptions}
                            value={projectManagerOptions.filter((o) =>
                              tempFilter.projectManager.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                projectManager: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Status Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("status", "Status")}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={t("select_status", "All Statuses")}
                            options={statusOptions}
                            value={statusOptions.filter((o) =>
                              tempFilter.status.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                status: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Project Type Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {projectFieldLabels.typeLabel}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${projectFieldLabels.typeLabel}`}
                            options={projectTypeOptions}
                            value={projectTypeOptions.filter((o) =>
                              tempFilter.projectType.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                projectType: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Project Phase Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {projectFieldLabels.phaseLabel}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${projectFieldLabels.phaseLabel}`}
                            options={projectPhaseOptions}
                            value={projectPhaseOptions.filter((o) =>
                              tempFilter.projectPhase.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                projectPhase: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Project Priority Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {projectFieldLabels.priorityLabel}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${projectFieldLabels.priorityLabel}`}
                            options={projectPriorityOptions}
                            value={projectPriorityOptions.filter((o) =>
                              tempFilter.projectPriority.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                projectPriority: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Workstream Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {projectFieldLabels.workstreamLabel}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${projectFieldLabels.workstreamLabel}`}
                            options={workstreamOptions}
                            value={workstreamOptions.filter((o) =>
                              tempFilter.workstream.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                workstream: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Project Category Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {projectFieldLabels.categoryLabel}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${projectFieldLabels.categoryLabel}`}
                            options={projectCategoryOptions}
                            value={projectCategoryOptions.filter((o) =>
                              tempFilter.projectCategory.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                projectCategory: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Tags Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("Tags")}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={`${t("all", "All")} ${t("Tags")}`}
                            options={tagsOptions}
                            value={tagsOptions.filter((o) =>
                              tempFilter.tags.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                tags: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Delay / Schedule Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("schedule_compliance", "Schedule")}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={t("select_delay", "Schedule Compliance")}
                            options={delayOptions}
                            value={delayOptions.filter((o) =>
                              tempFilter.delay.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                delay: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Budget Filter (Multi-select) */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("budget_compliance", "Budget")}
                        </span>
                        <div className="flex-1">
                          <Select
                            isMulti
                            placeholder={t("select_budget", "Budget Compliance")}
                            options={budgetOptions}
                            value={budgetOptions.filter((o) =>
                              tempFilter.budget.includes(o.value)
                            )}
                            onChange={(vals) =>
                              setTempFilter((prev) => ({
                                ...prev,
                                budget: getSelectedValues(vals),
                              }))
                            }
                            styles={reactSelectStyle}
                          />
                        </div>
                      </div>

                      {/* Date Range Filter */}
                      <div className="flex items-center gap-3 justify-between">
                        <span className="text-xs font-medium text-slate-500 w-28 shrink-0">
                          {t("end_date", "Date Range")}
                        </span>
                        <div className="flex-1">
                          <Popover>
                            <PopoverTrigger asChild className="w-full">
                              <Button
                                type="button"
                                variant={"outline"}
                                className="w-full h-8 py-1 px-2.5 rounded-lg border border-slate-200 text-xs text-left font-normal flex justify-between items-center"
                              >
                                <span className="truncate text-slate-700 font-semibold">
                                  {tempFilter.date?.from && tempFilter.date?.to
                                    ? `${dateFormater(
                                        tempFilter.date.from
                                      )} - ${dateFormater(tempFilter.date.to)}`
                                    : t("select_date_range", "Select Date Range")}
                                </span>
                                <img src={CalendarSvg} width={16} alt="calendar" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="z-50 bg-white p-0">
                              <div>
                                <Calendar
                                  mode="range"
                                  selected={tempFilter.date}
                                  onSelect={(e) =>
                                    setTempFilter((prev) => ({ ...prev, date: e }))
                                  }
                                  className="rounded-md border"
                                  captionLayout="dropdown-buttons"
                                  fromYear={2010}
                                  toYear={2050}
                                />
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="h-px bg-slate-100 my-1" />

                  {/* Actions Footer */}
                  <div className="flex items-center justify-between pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setTempFilter(initialFilterState);
                      }}
                      className="text-slate-500 hover:text-slate-700 font-bold text-xs cursor-pointer transition-colors"
                    >
                      {t("clear_all", "Clear all")}
                    </button>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTempFilter(filter);
                          setPopOverCLose(false);
                        }}
                        className="text-slate-500 hover:text-slate-700 font-semibold text-xs cursor-pointer transition-colors px-2 py-1"
                      >
                        {t("cancel", "Cancel")}
                      </button>
                      <Button
                        type="button"
                        onClick={() => {
                          setFilter(tempFilter);
                          setPopOverCLose(false);
                        }}
                        className="bg-[#535EF9] hover:bg-[#434ec9] text-white text-xs font-bold h-8 px-3 rounded-lg transition-colors border-0"
                      >
                        {t("apply_filters", "Apply filters")}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {(filterApplyed || searchParams.size > 0) && (
                <Button
                  size={"sm"}
                  className="w-5 h-5 rounded-full p-0 flex items-center justify-center bg-[#EA4335] hover:bg-[#EA4335] absolute -top-1.5 -right-1.5 z-10 shadow-xs cursor-pointer border-2 border-white"
                  variant={"outline"}
                  onClick={resetFilter}
                  title={t("clear_filters", "Clear all filters")}
                >
                  <img src={CrossSvg} className="w-2.5 h-2.5" alt="clear" />
                </Button>
              )}
            </div>

            <div className="w-full flex gap-2 relative items-center">
              <InputText
                className="pl-11 pr-3 mt-0 h-10 w-56 border-slate-200 placeholder:text-sm rounded-md focus:outline-secondary"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setSearchString(e.target.value);
                }}
                placeholder={t("search_project_by_name")}
              />
              <img
                src={SearchSvg}
                className="absolute h-5 left-3 top-3"
                width={"20px"}
                alt="search"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectFilter;
